import { RouteComponentProps } from "../../../../models/RouteComponent";
import { CardActions, CardOverflow, Typography } from "@mui/joy";
import { Button } from "primereact/button";
import React, { useEffect, useState } from "react";
import useApiService from "../../../../hooks/useApiService";
import { CompanyDetails, InductionRecord, Slide } from "../../../../models/DSF/Induction";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import "./styles.scss";
import { ClientRecord } from "../../../../models/ClientInfo";
import { Carousel } from "primereact/carousel";
import { formatDate } from "../../../../utils/DateUtils";

function SiteInduction(props: RouteComponentProps) {
  const { session } = props;
  const [slides, setSlides] = useState<Slide[]>([]);
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails | null>(null);
  const [selectedSlide, setSelectedSlide] = useState<"" | "all-slides">("");
  const { data, error, loading } = useApiService<InductionRecord>({
    method: "GET",
    url: "site-induction",
    apiName: "open",
    params: {
      clientId: session.client_id,
      siteId: session.site_id,
    },
    onSuccess: data => {
      setSlides(data.slides);
      setCompanyDetails(data.companyDetails);
    },
  });

  const {
    data: client,
    error: clientError,
    loading: clientLoading,
  } = useApiService<ClientRecord>({
    url: `client/${session.client_id}`,
    method: "GET",
    apiName: "core",
  });

  useEffect(() => {
    props.setGlobalLoader(loading || clientLoading);
  }, [loading, clientLoading]);

  const extendedSlides = React.useMemo(() => {
    if (!slides.length) return [];

    return [
      // Front custom slides
      {
        slide_id: "custom-1",
        title: "Intro",
        body: "",
        photo_bottom: "/static/img.png",
        photo_middle: "/static/SafetyInduction.png",
      },
      {
        slide_id: "custom-2",
        title: "Welcome",
        body: `<div style="display: flex; flex-direction: column; align-items: start; background-color: #17123a; width: 50%; margin: auto; color: white; font-size: 1.15rem;">
                   <p style="margin: 0.25rem 0 0; padding: 0 1rem; font-size: 1.75rem; font-weight: 700;">Project details</p>
                   <p style="margin: 0.5rem 0; padding: 0 1rem;">Company name: <strong>${companyDetails?.company_name}</strong> </p>
                   <p style="margin: 0.5rem 0; padding: 0 1rem;">Site name: <strong>${companyDetails?.site_name}</strong></p>
                   <p style="padding: 0 1rem; margin: 0.5rem 0;">Address: <strong>${companyDetails?.site_address}</strong></p>
                   <p style="padding: 0 1rem; margin: 0.5rem 0;">Start date: <strong>${formatDate(companyDetails?.site_start_date)}</strong></p>
                   <p style="padding: 0 1rem; margin: 0.5rem 0;">Est. end date: <strong>${formatDate(companyDetails?.site_end_date)}</strong></p>

            </div>`,
        photo_top: "/static/SafetyInductionWelcome.png",
      },
      {
        slide_id: "custom-3",
        title: "DSF intro",
        body: `<div style="display: flex; flex-direction: column; align-items: start; background-color: #17123a; width: 50%; margin: auto; color: white; font-size: 1.15rem;">
                <p style="margin: 0.5rem 0; padding: 0 1rem;">Our Digital Safety File, an eco-friendly, paperless solution, is central to our mission. 
                    This ever-evolving digital document embodies our dedication to health and safety management 
                    for this project. It includes continuous audits, thorough risk assessments, employee safety 
                    training, and strict PPE adherence, ensuring a safe working environment. From start to finish, 
                    each phase of the project adheres to high health and safety standards. The Digital Safety File 
                    serves as a comprehensive, environmentally conscious hub for all essential documentation, 
                    aligning with Cairnmead&apos;s rigorous criteria and legal requirements. Welcome aboard as we 
                    together strive for a safer, higher-quality, and greener future.</p>
                </div>`,
      },
      // Original slides
      ...slides,
      {
        slide_id: "custom-4",
        title: "think-safe-be-safe",
        photo_top: "/static/SafetyInductionThinkSafe.png",
      },
      {
        slide_id: "custom-5",
        title: "message",
        photo_top: "/static/SafetyInductionSign.png",
      },
      {
        slide_id: "custom-6",
        title: "message",
        photo_top: "/static/SafetyInductionThankYou.png",
      },
    ];
  }, [slides]);

  interface RenderSlide extends Slide {
    photo_bottom?: string;
    photo_middle?: string;
    photo_top?: string;
    slide_id: "custom-1";
    body: string;
  }

  const renderSlide = (slide: RenderSlide) => {
    if (
      ["custom-1", "custom-2", "custom-3", "custom-4", "custom-5", "custom-6"].includes(
        slide.slide_id
      )
    ) {
      return (
        <div className={"slide-carousel"}>
          <div className={"slide-header"}>
            <img src={"/DSF_Logo-Low-trimmed2.ico"} alt={"DSF logo"} />
            <div>
              <Typography level="h3" component="h2">
                {companyDetails?.company_name}
              </Typography>

              <Typography level="h4" component="h2">
                Site: {companyDetails?.site_name}
              </Typography>
              <Typography level={"body-lg"} component={"h3"}>
                Title: {slide.title}
              </Typography>
            </div>
            {client ? <img src={client.photo} alt={client.name} /> : <div />}
          </div>
          <div className={"slide-content"}>
            <div>
              {slide.photo_top && (
                <div className={"slide-photo-container"}>
                  <img
                    className={"slide-photo"}
                    src={slide.photo_top}
                    alt={slide.title}
                    onError={e => {
                      e.currentTarget.src =
                        "https://ionicframework.com/docs/img/demos/thumbnail.svg";
                    }}
                  />
                </div>
              )}

              <div dangerouslySetInnerHTML={{ __html: slide.body }} />
              {slide.photo_middle && (
                <div className={"slide-photo-container"}>
                  <img
                    className={"slide-photo"}
                    src={slide.photo_middle}
                    alt={slide.title}
                    onError={e => {
                      e.currentTarget.src =
                        "https://ionicframework.com/docs/img/demos/thumbnail.svg";
                    }}
                  />
                </div>
              )}
              {slide.photo_bottom && (
                <div className={"slide-photo-container"}>
                  <img
                    className={"slide-photo"}
                    src={slide.photo_bottom}
                    alt={slide.title}
                    onError={e => {
                      e.currentTarget.src =
                        "https://ionicframework.com/docs/img/demos/thumbnail.svg";
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className={"slide-carousel"}>
        <div className={"slide-header"}>
          <img src={"/DSF_Logo-Low-trimmed2.ico"} alt={"DSF logo"} />
          <div>
            <Typography level="h3" component="h2">
              {companyDetails?.company_name}
            </Typography>

            <Typography level="h4" component="h2">
              Site: {companyDetails?.site_name}
            </Typography>
            <Typography level={"body-lg"} component={"h3"}>
              Title: {slide.title}
            </Typography>
          </div>
          {client ? <img src={client.photo} alt={client.name} /> : <div />}
        </div>
        <div className={"slide-content"}>
          <div>
            <div className={"slide-photo-container"}>
              <img
                className={"slide-photo"}
                src={slide.photo || "https://ionicframework.com/docs/img/demos/thumbnail.svg"}
                alt={slide.title}
                onError={e => {
                  e.currentTarget.src = "https://ionicframework.com/docs/img/demos/thumbnail.svg";
                }}
              />
            </div>
            <div className={"slide-body"} dangerouslySetInnerHTML={{ __html: slide.body }} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Typography level="h2" component="h1">
        Site induction
      </Typography>

      {error || clientError ? (
        <div className={"error-wrapper"}>
          <h1>There was an error fetching the site induction slides</h1>
        </div>
      ) : (
        <>
          <div className={"page-download-actions"}>
            <Button
              label={"View induction"}
              icon={"pi pi-eye"}
              className="p-button-outlined"
              style={{ fontSize: "small" }}
              onClick={() => {
                setSelectedSlide("all-slides");
              }}
            />
            <Button
              name="button"
              icon="pi pi-save"
              label="Download PDF"
              className="p-button-outlined"
              size="small"
              disabled={true}
              style={{
                fontSize: "small",
                color: "#2dd36f",
              }}
              onClick={() => {}}
            />
          </div>
          {data && (
            <DataTable value={slides}>
              <Column field="title" header="All inductions" />
            </DataTable>
          )}
        </>
      )}

      {selectedSlide && (
        <Dialog
          onHide={() => setSelectedSlide("")}
          header={"Induction"}
          visible={!!selectedSlide}
          style={{ width: "70vw" }}
        >
          <>
            <Carousel
              value={extendedSlides}
              numScroll={1}
              numVisible={1}
              itemTemplate={renderSlide}
            />
          </>
        </Dialog>
      )}
    </>
  );
}

export default SiteInduction;
